const RELOAD_GUARD_KEY = 'staleBuildReloadedAt';
const RELOAD_GUARD_WINDOW_MS = 10_000;

/**
 * A tab left open across a deployment still holds the previous build's index.html,
 * so its lazy chunks point at hashed files that no longer exist on Static Web Apps.
 * The dynamic import then fails and the route never renders. Reloading pulls the new
 * index.html and the hashes that go with it.
 *
 * The timestamp guard stops a reload loop if the chunk is missing for some other
 * reason (a genuinely broken deploy), where reloading would never fix anything.
 */
export function registerStaleBuildRecovery(): void {
	const reload = () => {
		const lastReload = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);

		if (Date.now() - lastReload < RELOAD_GUARD_WINDOW_MS) {
			return;
		}

		sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
		window.location.reload();
	};

	// Fired by Vite when a preloaded/lazy chunk fails to load.
	window.addEventListener('vite:preloadError', (event) => {
		event.preventDefault();
		reload();
	});

	// Dynamic imports triggered outside Vite's preload helper surface here instead.
	window.addEventListener('unhandledrejection', (event) => {
		const message = String(event.reason?.message ?? event.reason ?? '');

		if (/Failed to fetch dynamically imported module|error loading dynamically imported module/i.test(message)) {
			reload();
		}
	});
}
